import { useMemo } from 'react';
import { BN, MarketType, ZERO } from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import useDriftClient from './useDriftClient';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { usePositionsByUserKey } from './query/accountTradingData/usePositions';
import { useOrdersByUserKey } from './query/accountTradingData/useOrders';

/**
 * Hook that aggregates all isolated position deposits for a user.
 *
 * This calculates the sum of `getIsolatedPerpPositionTokenAmount(marketIndex)`
 * for all isolated perp positions. The returned value represents USDC deposited
 * as margin for isolated positions, which is not included in `getNetSpotMarketValue()`.
 *
 * @param userKey - Optional user key to get deposits for. If not provided, uses current user.
 * @returns Total isolated position deposits as a BN (in USDC token amount, with QUOTE_PRECISION)
 */
export const useIsolatedPositionDepositsTotal = (userKey?: string): BN => {
	const driftClient = useDriftClient();
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const targetUserKey = userKey ?? currentUserKey ?? '';
	const accountData = useDriftAccountStore((s) => s.accounts[targetUserKey]);
	const { data: ordersResult } = useOrdersByUserKey(targetUserKey || undefined);
	const openOrders = ordersResult?.openOrders;
	const { data: positionsResult } = usePositionsByUserKey(
		targetUserKey || undefined
	);
	const openPerpPositions = positionsResult?.openPerpPositions;

	const totalIsolatedDeposits = useMemo(() => {
		if (!driftClient || !openPerpPositions || !accountData?.client) {
			return ZERO;
		}

		try {
			const user = accountData.client;
			const perpOrderMarketIndexes = (openOrders ?? [])
				.filter((order) => ENUM_UTILS.match(order.marketType, MarketType.PERP))
				.map((order) => order.marketIndex);

			const uniqueMarketIndexes = new Set(
				openPerpPositions
					.map((position) => position.marketIndex)
					.concat(perpOrderMarketIndexes)
			);

			const totalIsolatedPositionsDeposits = Array.from(
				uniqueMarketIndexes
			).reduce((total, marketIndex) => {
				const perpPosition = user.getPerpPosition(marketIndex);
				// markets with open orders but no positions yet, will still appear an a defined perpPosition
				if (!perpPosition || !user.isPerpPositionIsolated(perpPosition)) {
					return total;
				}

				const tokenAmount = user.getIsolatePerpPositionTokenAmount(marketIndex);
				return total.add(tokenAmount ?? ZERO);
			}, ZERO);

			return totalIsolatedPositionsDeposits;
		} catch (e) {
			console.error('Error calculating total isolated position deposits', e);
			return ZERO;
		}
	}, [driftClient, openPerpPositions, accountData?.client, openOrders]);

	return totalIsolatedDeposits;
};
